import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Table, Alert } from 'react-bootstrap';
import { db } from '../firebase';
import { collection, addDoc, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { getAuth, User } from 'firebase/auth';
import './Cashbook.css';

const CashbookPage: React.FC = () => {
  const [entries, setEntries] = useState<any[]>([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [income, setIncome] = useState('');
  const [expense, setExpense] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (currentUser) {
      const q = query(collection(db, 'cashbookEntries'), where('uid', '==', currentUser.uid), orderBy('date', 'desc'));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const entriesData = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setEntries(entriesData);
      });
      return unsubscribe;
    }
  }, [currentUser]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) {
      setError('You must be logged in to add entries.');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      await addDoc(collection(db, 'cashbookEntries'), {
        uid: currentUser.uid,
        date,
        description,
        income: parseFloat(income) || 0,
        expense: parseFloat(expense) || 0,
      });
      setDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setIncome('');
      setExpense('');
    } catch (err: any) {
      setError(err.message || 'Failed to add entry.');
    } finally {
      setLoading(false);
    }
  };

  const getBalance = () => {
    return entries.reduce((acc, entry) => acc + entry.income - entry.expense, 0);
  };

  return (
    <Container className="py-5">
      <h2 className="text-center mb-4">Cashbook</h2>
      {error && <Alert variant="danger">{error}</Alert>}
      {currentUser ? (
        <Form onSubmit={handleSubmit} className="mb-4">
          <Form.Group className="mb-3">
            <Form.Label>Date</Form.Label>
            <Form.Control type="date" value={date} onChange={e => setDate(e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control type="text" placeholder="Enter description" value={description} onChange={e => setDescription(e.target.value)} required />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Income</Form.Label>
            <Form.Control type="number" placeholder="Enter income" value={income} onChange={e => setIncome(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Expense</Form.Label>
            <Form.Control type="number" placeholder="Enter expense" value={expense} onChange={e => setExpense(e.target.value)} />
          </Form.Group>
          <Button variant="primary" type="submit" disabled={loading}>{loading ? 'Adding...' : 'Add Entry'}</Button>
        </Form>
      ) : (
        <Alert variant="warning">Please log in to use the cashbook.</Alert>
      )}

      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Income</th>
            <th>Expense</th>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => (
            <tr key={entry.id}>
              <td>{entry.date}</td>
              <td>{entry.description}</td>
              <td>{entry.income > 0 ? entry.income.toFixed(2) : '-'}</td>
              <td>{entry.expense > 0 ? entry.expense.toFixed(2) : '-'}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th colSpan={2}>Total</th>
            <th>{entries.reduce((acc, entry) => acc + entry.income, 0).toFixed(2)}</th>
            <th>{entries.reduce((acc, entry) => acc + entry.expense, 0).toFixed(2)}</th>
          </tr>
          <tr>
            <th colSpan={3}>Balance</th>
            <th>{getBalance().toFixed(2)}</th>
          </tr>
        </tfoot>
      </Table>
    </Container>
  );
};

export default CashbookPage;
